import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {lastCreatedDocumentId$} from 'models/documents/create';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/documents/withdrawal/grid/table';
import {WithdrawalGridFormattedRow, WithdrawalGridRawRow} from 'types';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: WithdrawalGridRawRow[] | undefined,
        t: TFunction,
        lastCreatedDocumentId?: string | null,
    ) => WithdrawalGridFormattedRow[];
    headerControls?: ReactNode;
    headerControlsAlignItems?: string;
}

export const WithdrawalGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    headerControlsAlignItems,
}: Props) => {
    useGate(Gate);
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings, dictionary} = useStore(tableState$);
    const lastCreatedDocumentId = useStore(lastCreatedDocumentId$);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [dictionary, filters, sortings, t],
    );

    const mappedData = useMemo(() => dataMapper(data?.content, t, lastCreatedDocumentId), [
        data,
        dataMapper,
        lastCreatedDocumentId,
        t,
    ]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            hasNext={data?.hasNext}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={isLoading}
            userConfig={{
                gridName: 'withdrawalGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            headerControls={headerControls}
            headerControlsAlignItems={headerControlsAlignItems}
            cellContainerSettings="width: 100%; min-height: 60px; word-break: break-word;"
        />
    );
};
